'use client'

import {createContext, type CSSProperties, type ReactNode, type RefObject, useContext, useId, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {createDocumentEditorOwner, type DocumentEditorOwner, getEditorScrollOwner, registerDocumentEditor, unregisterDocumentEditor} from './documentEditorOwnership'
import styles from './DocumentEditorFrame.module.css'
import {getHistoryEntryKey, getPushedEditorReturnPosition, readScrollPosition, type ScrollPosition} from './historyScrollRestoration'
import {useSelectionPointerCommit} from './useSelectionPointerCommit'
import {suppressNextCompatibleClick} from './compatibleClick'
import {readEditorHandoff, useSmoothPanelResize} from './useSmoothPanelResize'

const ParentEditorContext = createContext<string | null>(null)

interface DocumentEditorFrameProps {
    /** `inline` renders in place inside the app shell; `page`/`dialog` portal over it. */
    layout: 'page' | 'dialog' | 'inline'
    /** Local utilities are not recreated by URL/history; retain their opener's snapshot. */
    historyMode?: 'route' | 'local'
    rootRef: RefObject<HTMLDivElement | null>
    label?: string
    header: ReactNode
    children: ReactNode
    footer?: ReactNode
    className?: string
    contentClassName?: string
    zIndex?: number
    desktopMaxWidth?: CSSProperties['maxInlineSize']
    /**
     * `top` keeps the dialog header in place while the content grows or shrinks below it, and resumes the height a
     * sibling section handed off (`handOffEditorHeight`), so sections of one record switch as one dialog.
     */
    align?: 'center' | 'top'
    /** Keeps the footer's action in reach while a long body scrolls, and at the bottom of a short one. */
    stickyFooter?: boolean
    onBackdropClick?: () => void
}

/** A document on compact screens, with the same form tree inside a desktop dialog or an app page. */
export function DocumentEditorFrame(props: DocumentEditorFrameProps) {
    return props.layout === 'inline' ? <InlineEditorFrame {...props}/> : <OverlayEditorFrame {...props} layout={props.layout}/>
}

/** A page inside the app shell: no portal, no ownership, the app keeps its navigation and window scroll. */
function InlineEditorFrame({
    rootRef, label, header, children, footer, className, contentClassName, desktopMaxWidth = '56rem',
}: DocumentEditorFrameProps) {
    return (
        <section
            ref={rootRef}
            data-document-editor=""
            data-layout="inline"
            aria-label={label}
            className={styles.root}
            style={{'--fitro-editor-desktop-width': desktopMaxWidth} as CSSProperties}
        >
            <div className={[styles.surface, className].filter(Boolean).join(' ')}>
                <div className={styles.flow}>
                    <div data-editor-header="" className={styles.header}>{header}</div>
                    <div data-editor-body="" className={[styles.body, contentClassName].filter(Boolean).join(' ')}>{children}</div>
                    {footer ? <div data-editor-footer="" className={styles.footer}>{footer}</div> : null}
                </div>
            </div>
        </section>
    )
}

function OverlayEditorFrame({
    layout, rootRef, label, header, children, footer, className, contentClassName, historyMode = 'route',
    zIndex = 60, desktopMaxWidth = '56rem', align = 'center', stickyFooter = false, onBackdropClick,
}: DocumentEditorFrameProps & {layout: 'page' | 'dialog'}) {
    const id = useId()
    const parentId = useContext(ParentEditorContext)
    const [host, setHost] = useState<HTMLElement | null>(null)
    const hostRef = useRef<HTMLElement | null>(null)
    const ownerRef = useRef<DocumentEditorOwner | null>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const surfaceRef = useRef<HTMLDivElement>(null)
    const enteringRef = useRef(false)
    const suspendedPositionRef = useRef<ScrollPosition | null>(null)
    const suspendedEntryRef = useRef<string | null>(null)
    const pressedBackdropRef = useRef(false)
    const backdrop = useSelectionPointerCommit(
        () => onBackdropClick?.(), layout !== 'dialog' || !onBackdropClick, suppressNextCompatibleClick,
    )

    useLayoutEffect(() => {
        // Keep the portal target stable through Activity/StrictMode Effect replay.
        // Replacing it would remount the entire draft when a cached route returns.
        const element = hostRef.current ??= document.createElement('div')
        ownerRef.current ??= createDocumentEditorOwner(id, parentId, layout, zIndex, getHistoryEntryKey(), historyMode, getPushedEditorReturnPosition())
        element.setAttribute('data-document-editor-host', '')
        document.body.appendChild(element)
        enteringRef.current = true
        setHost(element)
        return () => {
            suspendedEntryRef.current = getHistoryEntryKey()
            const target = getEditorScrollOwner(ownerRef.current?.root ?? null)
            if (target) suspendedPositionRef.current = readScrollPosition(target)
            element.remove()
            if (ownerRef.current) unregisterDocumentEditor(ownerRef.current)
        }
        // Geometry is updated separately; it must never replace the portal host.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, parentId])

    useLayoutEffect(() => {
        const owner = ownerRef.current
        if (!host || !owner || !rootRef.current) return
        owner.root = rootRef.current
        owner.layout = layout
        owner.zIndex = zIndex
        registerDocumentEditor(owner)
        const entering = enteringRef.current
        enteringRef.current = false
        if (owner.root.dataset.editorActive === 'true') {
            if (entering) {
                // New editors start at the top. Activity replay reuses the same
                // draft and scroll position instead of erasing the visit.
                // A section switched from its tab keeps focus on the tabs, so arrow keys keep moving between sections.
                const selectedTab = align === 'top' && readEditorHandoff()?.tabFocused
                    ? owner.root.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
                    : null
                const focusTarget = selectedTab ?? headerRef.current
                focusTarget?.focus({preventScroll: true})
                const target = getEditorScrollOwner(owner.root)
                if (target && suspendedPositionRef.current && suspendedEntryRef.current === getHistoryEntryKey()) {
                    const position = suspendedPositionRef.current
                    target.scrollTo({top: position.y, left: position.x, behavior: 'instant'})
                } else {
                    target?.scrollTo({top: 0, left: 0, behavior: 'instant'})
                }
            } else if (!owner.root.contains(document.activeElement)) {
                headerRef.current?.focus({preventScroll: true})
            }
        }
        return () => unregisterDocumentEditor(owner)
    }, [align, host, layout, rootRef, zIndex])

    useSmoothPanelResize(surfaceRef, Boolean(host) && layout === 'dialog', align === 'top')

    if (!host) return null
    return createPortal(
        <ParentEditorContext.Provider value={id}><div
            ref={rootRef}
            data-document-editor=""
            data-modal-root=""
            data-layout={layout}
            data-align={align}
            data-sticky-footer={stickyFooter ? '' : undefined}
            role={layout === 'dialog' ? 'dialog' : 'main'}
            aria-modal={layout === 'dialog' ? true : undefined}
            aria-label={label}
            className={styles.root}
            style={{zIndex, '--fitro-editor-desktop-width': desktopMaxWidth} as CSSProperties}
            onPointerDown={event => {
                pressedBackdropRef.current = event.target === event.currentTarget
                if (pressedBackdropRef.current) backdrop.onPointerDown(event)
            }}
            onPointerMove={backdrop.onPointerMove}
            onPointerUp={backdrop.onPointerUp}
            onPointerCancel={backdrop.onPointerCancel}
            onLostPointerCapture={backdrop.onLostPointerCapture}
            onClick={event => {
                // A text selection dragged out of a field ends on the backdrop; only a press that started there closes.
                const pressedBackdrop = pressedBackdropRef.current
                pressedBackdropRef.current = false
                if (event.target === event.currentTarget && (pressedBackdrop || event.detail === 0)) backdrop.onClick(event)
            }}
        >
            <div ref={surfaceRef} className={[styles.surface, className].filter(Boolean).join(' ')}>
                <div data-editor-scroll="" className={styles.flow}>
                    <div ref={headerRef} data-editor-header="" tabIndex={-1} className={styles.header}>{header}</div>
                    <div data-editor-body="" className={[styles.body, contentClassName].filter(Boolean).join(' ')}>{children}</div>
                    {footer ? <div data-editor-footer="" className={styles.footer}>{footer}</div> : null}
                </div>
            </div>
        </div></ParentEditorContext.Provider>, host
    )
}
